import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getHeatmapSettings,
  updateHeatmapSettings,
  getHeatmapSummary,
  getHeatmapDaily,
  getHeatmapDensity,
  getScrollReach,
  getHoverDwell,
  getAttention,
  toExclusiveToDate,
  isHeatmapEntitlementDenied
} from '../../../services/heatmap.service';
import { normalizeHeatmapError } from '../shared/heatmapViewUtils';

/**
 * Custom hook to manage Heatmap settings, summary metrics, and coordinate density grids.
 *
 * @param {object} params
 * @param {string} params.organizationId
 * @param {string} params.siteCollectionId
 * @param {string} params.fromDate - User selected start date (YYYY-MM-DD)
 * @param {string} params.toDate - User selected end date (YYYY-MM-DD)
 * @param {string} [params.pageId] - Current page selected for heatmap overlay
 * @param {number} [params.layoutVersion=1] - Page layout version
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - Whether automated queries should run
 * @param {boolean} [options.enableMetrics=true] - Whether metric queries should run
 */
export function useHeatmapData({
  organizationId,
  siteCollectionId,
  fromDate,
  toDate,
  pageId,
  layoutVersion = 1,
  deviceClass = 'all'
} = {}, options = {}) {
  const enabled = options.enabled !== false;
  const enableMetrics = options.enableMetrics !== false;

  // Settings State
  const [settings, setSettings] = useState(null);
  const [etag, setEtag] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [isConcurrencyConflict, setIsConcurrencyConflict] = useState(false);

  // Metrics State
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [density, setDensity] = useState([]);
  const [scrollReach, setScrollReach] = useState(null);
  const [hoverDwell, setHoverDwell] = useState(null);
  const [attention, setAttention] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [entitlementDenied, setEntitlementDenied] = useState(false);

  // In-memory query cache keyed by org:sc:page:layout:from:to
  const metricsCacheRef = useRef(new Map());

  // Abort controllers for in-flight cancellation
  const settingsAbortControllerRef = useRef(null);
  const metricsAbortControllerRef = useRef(null);

  // 1. Load Settings
  const fetchSettings = useCallback(async () => {
    if (!organizationId || !siteCollectionId) {
      setSettings(null);
      setEtag(null);
      return;
    }

    if (settingsAbortControllerRef.current) {
      settingsAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    settingsAbortControllerRef.current = controller;
    const signal = controller.signal;

    setSettingsLoading(true);
    setSettingsError(null);
    setIsConcurrencyConflict(false);

    try {
      const result = await getHeatmapSettings(organizationId, siteCollectionId, { signal });
      if (signal.aborted) return;
      setSettings(result.settings || { captureEnabled: false, advancedCaptureEnabled: false, pages: [], actions: [] });
      setEtag(result.etag);
    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) return;
      if (isHeatmapEntitlementDenied(err)) {
        setEntitlementDenied(true);
      }
      setSettingsError(normalizeHeatmapError(err, 'Failed to load heatmap settings'));
    } finally {
      if (settingsAbortControllerRef.current === controller || !signal.aborted) {
        setSettingsLoading(false);
      }
    }
  }, [organizationId, siteCollectionId]);

  useEffect(() => {
    if (enabled && organizationId && siteCollectionId) {
      fetchSettings();
    } else {
      setSettings(null);
      setEtag(null);
      setSettingsError(null);
      setSettingsLoading(false);
    }
    return () => {
      if (settingsAbortControllerRef.current) {
        settingsAbortControllerRef.current.abort();
      }
      setSettingsLoading(false);
    };
  }, [enabled, organizationId, siteCollectionId, fetchSettings]);

  // 2. Save Settings with Optimistic Concurrency
  const saveSettings = useCallback(async (payload) => {
    if (!organizationId || !siteCollectionId) {
      throw new Error('Organization and Site Collection are required');
    }

    setSettingsLoading(true);
    setSettingsError(null);
    setIsConcurrencyConflict(false);

    try {
      const result = await updateHeatmapSettings(organizationId, siteCollectionId, payload, etag);
      setSettings(payload);
      if (result.etag) {
        setEtag(result.etag);
      }
      return { success: true, etag: result.etag };
    } catch (err) {
      if (err?.status === 409 || err?.status === 412) {
        setIsConcurrencyConflict(true);
        const conflictErr = new Error('Settings were modified by another administrator. Please reload before saving.');
        conflictErr.status = err?.status;
        setSettingsError(conflictErr.message);
        throw conflictErr;
      }
      const msg = normalizeHeatmapError(err, 'Failed to update heatmap settings');
      setSettingsError(msg);
      throw err;
    } finally {
      setSettingsLoading(false);
    }
  }, [organizationId, siteCollectionId, etag]);

  // 3. Load Metrics (Summary, Daily Trends, Coordinate Density) Sequentially with Cancellation
  const fetchMetrics = useCallback(async (forceRefresh = false) => {
    if (!enabled || !enableMetrics || !organizationId || !siteCollectionId || !fromDate || !toDate) {
      return;
    }

    // Cancel any previous in-flight metrics request
    if (metricsAbortControllerRef.current) {
      metricsAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    metricsAbortControllerRef.current = controller;
    const signal = controller.signal;

    // Convert to exclusive upper bound for backend query
    const exclusiveToDate = toExclusiveToDate(toDate);
    const queryKey = `${organizationId}:${siteCollectionId}:${pageId || 'none'}:${layoutVersion}:${fromDate}:${exclusiveToDate}:${deviceClass}`;

    if (!forceRefresh && metricsCacheRef.current.has(queryKey)) {
      const cached = metricsCacheRef.current.get(queryKey);
      setSummary(cached.summary);
      setDaily(cached.daily);
      setDensity(cached.density);
      setScrollReach(cached.scrollReach || null);
      setHoverDwell(cached.hoverDwell || null);
      setAttention(cached.attention || null);
      setMetricsLoading(false);
      setMetricsError(null);
      return;
    }

    setMetricsLoading(true);
    setMetricsError(null);

    try {
      // Step 1: Summary query
      let summaryData = null;
      try {
        summaryData = await getHeatmapSummary(organizationId, siteCollectionId, fromDate, exclusiveToDate, { signal });
      } catch (err) {
        if (err.name === 'AbortError' || signal.aborted) return;
        if (isHeatmapEntitlementDenied(err)) {
          setEntitlementDenied(true);
        }
        throw err;
      }

      if (signal.aborted) return;

      // Step 2: Daily trend query (sequential)
      let dailyData = [];
      try {
        const dailyRes = await getHeatmapDaily(organizationId, siteCollectionId, fromDate, exclusiveToDate, { signal });
        dailyData = Array.isArray(dailyRes) ? dailyRes : (summaryData?.daily || []);
      } catch (err) {
        if (err.name === 'AbortError' || signal.aborted) return;
        dailyData = summaryData?.daily || [];
      }

      if (signal.aborted) return;

      // Step 3: Density, Scroll Reach, Hover Dwell, and Attention queries (if pageId is present)
      let densityData = [];
      let scrollReachData = null;
      let hoverDwellData = null;
      let attentionData = null;

      if (pageId) {
        try {
          const densityRes = await getHeatmapDensity(organizationId, siteCollectionId, {
            pageId,
            layoutVersion,
            fromDate,
            toDate: exclusiveToDate,
            signal
          });
          densityData = Array.isArray(densityRes) ? densityRes : [];
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          densityData = [];
        }

        try {
          const rawScroll = await getScrollReach(organizationId, siteCollectionId, {
            pageId,
            layoutVersion,
            fromDate,
            toDate: exclusiveToDate,
            signal
          });
          if (rawScroll) {
            const points = Array.isArray(rawScroll.reachPoints)
              ? rawScroll.reachPoints.map((pt) => {
                  const pct = pt.percentile != null ? pt.percentile : pt.depthPercent;
                  const count = pt.sessionCount != null ? pt.sessionCount : (pt.reachedVisits || 0);
                  const rate = rawScroll.eligibleVisits > 0 ? count / rawScroll.eligibleVisits : (pt.reachRate || 0);
                  return {
                    depthPercent: pct,
                    percentile: pct,
                    reachedVisits: count,
                    sessionCount: count,
                    reachRate: rate
                  };
                })
              : (rawScroll.depthDistribution || []);
            scrollReachData = {
              ...rawScroll,
              reachPoints: points,
              depthDistribution: points,
              medianScrollDepthPercent: rawScroll.medianScrollDepthPercent ?? rawScroll.medianScrollDepth ?? 0
            };
          }
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          scrollReachData = null;
        }

        try {
          const rawHover = await getHoverDwell(organizationId, siteCollectionId, {
            pageId,
            layoutVersion,
            fromDate,
            toDate: exclusiveToDate,
            signal
          });
          if (rawHover) {
            const elements = (rawHover.elements || []).map((el) => ({
              ...el,
              selector: el.selector || el.elementSelector || '',
              elementSelector: el.elementSelector || el.selector || '',
              targetName: el.targetName || el.elementSelector || el.selector || 'Interactive Element',
              hoverCount: el.hoverCount || 0,
              uniqueVisitors: el.uniqueVisitors ?? el.hoverCount ?? 0,
              medianDwellMs: el.medianDwellMs ?? el.dwellP50Ms ?? 0,
              p75DwellMs: el.p75DwellMs ?? el.dwellP75Ms ?? 0,
              avgDwellMs: el.avgDwellMs ?? el.medianDwellMs ?? 0
            }));
            hoverDwellData = {
              ...rawHover,
              elements
            };
          }
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          hoverDwellData = null;
        }

        try {
          const rawAttention = await getAttention(organizationId, siteCollectionId, {
            pageId,
            layoutVersion,
            fromDate,
            toDate: exclusiveToDate,
            signal
          });
          if (rawAttention) {
            const sections = (rawAttention.sections || []).map((sec) => {
              const medMs = sec.medianAttentionMs ?? sec.exposureP50Ms ?? 0;
              const p90Ms = sec.p90AttentionMs ?? sec.exposureP90Ms ?? 0;
              const visits = sec.totalVisits ?? sec.activeVisits ?? sec.totalImpressions ?? 0;
              return {
                ...sec,
                sectionId: sec.sectionId || '',
                sectionLabel: sec.sectionLabel || sec.sectionId || 'Section',
                medianAttentionMs: medMs,
                p90AttentionMs: p90Ms,
                totalVisits: visits,
                totalImpressions: visits,
                avgViewTimeSeconds: sec.avgViewTimeSeconds ?? (medMs > 0 ? medMs / 1000 : 0),
                attentionScore: sec.attentionScore ?? Math.min(100, Math.round((medMs / 15000) * 100))
              };
            });
            attentionData = {
              ...rawAttention,
              sections,
              attentionType: rawAttention.attentionType || rawAttention.measurementBasis || 'screen_exposure_not_eye_tracking',
              measurementBasis: rawAttention.measurementBasis || rawAttention.attentionType || 'screen_exposure_not_eye_tracking'
            };
          }
        } catch (err) {
          if (err.name === 'AbortError' || signal.aborted) return;
          attentionData = null;
        }
      }

      if (signal.aborted) return;

      const bundle = {
        summary: summaryData,
        daily: dailyData,
        density: densityData,
        scrollReach: scrollReachData,
        hoverDwell: hoverDwellData,
        attention: attentionData
      };
      metricsCacheRef.current.set(queryKey, bundle);

      setSummary(summaryData);
      setDaily(dailyData);
      setDensity(densityData);
      setScrollReach(scrollReachData);
      setHoverDwell(hoverDwellData);
      setAttention(attentionData);
    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) {
        return;
      }
      if (isHeatmapEntitlementDenied(err)) {
        setEntitlementDenied(true);
      }
      setMetricsError(normalizeHeatmapError(err, 'Failed to fetch heatmap metrics'));
    } finally {
      if (metricsAbortControllerRef.current === controller || !signal.aborted) {
        setMetricsLoading(false);
      }
    }
  }, [enabled, enableMetrics, organizationId, siteCollectionId, fromDate, toDate, pageId, layoutVersion, deviceClass]);

  useEffect(() => {
    if (enabled && enableMetrics && organizationId && siteCollectionId && fromDate && toDate) {
      fetchMetrics();
    } else {
      setMetricsLoading(false);
      if (!organizationId || !siteCollectionId) {
        setSummary(null);
        setDaily([]);
        setDensity([]);
        setScrollReach(null);
        setHoverDwell(null);
        setAttention(null);
        setMetricsError(null);
      }
    }
    return () => {
      if (metricsAbortControllerRef.current) {
        metricsAbortControllerRef.current.abort();
      }
      setMetricsLoading(false);
    };
  }, [enabled, enableMetrics, organizationId, siteCollectionId, fromDate, toDate, fetchMetrics]);

  return {
    // Settings
    settings,
    etag,
    settingsLoading,
    settingsError,
    isConcurrencyConflict,
    saveSettings,
    refetchSettings: fetchSettings,

    // Metrics
    summary,
    daily,
    density,
    scrollReach,
    hoverDwell,
    attention,
    metricsLoading,
    metricsError,
    entitlementDenied,
    refetchMetrics: () => fetchMetrics(true)
  };
}
